import { useMenuState } from "@szhsin/react-menu"
import { UseQueryResult } from "@tanstack/react-query"
import { AuthData, SocialAccount } from "api/resources/auth/types"
import { UserOrganizations } from "api/resources/growthspace/types"
// import { DueAssignmentList } from "api/resources/assignments/types"
import {
  Enrollment,
  LearningRestrictions,
} from "api/resources/learningItems/types"
import { OfferLetterType } from "api/resources/offerLetter/types"
import { Profile } from "api/resources/profile/types"
import { Email, UserPreference } from "api/resources/settings/types"
import { APIResponse } from "api/types"
import { AuthManager } from "features/Authentication/AuthManager"
import { ITOParticipationDetails } from "features/ITO/useITOAccess"
import React, { useContext } from "react"

export type PlatformSettings = null | { [x: string]: UserPreference }

type Context = {
  authInfo: AuthData | null
  setAuthInfo: (auth: AuthData, options?: { skipRemount?: boolean }) => void
  isAuthenticated: boolean
  itoParticipationDetails: ITOParticipationDetails
  emails: Email[]
  updateEmails: (emails: Email[]) => void
  // null means loading
  enrollments: UseQueryResult<APIResponse<Enrollment[]>, unknown>
  profile: Profile
  fetchProfile: () => void
  notificationsPopupMenuState: ReturnType<typeof useMenuState>
  learningRestrictions?: LearningRestrictions[]
  platformSettings: PlatformSettings
  // dueAssignmentList?: DueAssignmentList[]
  socialAccounts: SocialAccount[]
  refetchSocialAccounts: () => Promise<any>
  offerLetter: UseQueryResult<OfferLetterType, unknown>
  listGrowthspaceQuery: UseQueryResult<UserOrganizations[]> | undefined
  growthspaces: UserOrganizations[]
  currentGrowthspace?: UserOrganizations | null
  switchGrowthspace: (workspaceId: string) => Promise<void>
  isGrowthSpaceUser?: boolean
  isNcte?: boolean
}

export const defaultContextValue: Context = {
  authInfo: null,
  logout: () => {},
  setAuthInfo: auth => AuthManager.setPlatformAuth(auth),
  isAuthenticated: false,
  notificationsPopupMenuState: [
    { state: undefined, endTransition: () => {} },
    () => {},
  ],
  emails: [],
  updateEmails: () => {},
  // @ts-expect-error this is safe to do
  enrollments: {},
  itoParticipationDetails: {
    currentITOAttemptStatus: undefined,
    pastYears: {
      isITO2022Participant: undefined,
    },
    itoRegistrationDetailsLoaded: false,
    itoRegistrationDetails: undefined,
    registrationStatus: "notStarted",
    waitlistStatus: undefined,
    timeline: undefined,
  },
  profile: {
    id: "",
    city: null,
    yearsOfExperience: null,
    totalWorkingExperience: 0,
    careerAspiration: null,
    countriesWorkedIn: [],
    country: null,
    currentWorkingExperience: null,
    dateOfBirth: null,
    gender: null,
    isVerified: false,

    lookingForJobs: false,
    nationality: null,
    phoneNumber: null,
    picture: null,
    state: null,
    user: {
      uuid: "",
      email: "",
      firstName: "",
      lastName: null,
      middleName: "",
      username: "",
    },
  },
  learningRestrictions: [],
  // dueAssignmentList: [],
  fetchProfile: () => {},
  platformSettings: null,
  socialAccounts: [],
  refetchSocialAccounts: async () => {},
  offerLetter: {} as UseQueryResult<OfferLetterType, unknown>,
  listGrowthspaceQuery: undefined,
  growthspaces: [],
  currentGrowthspace: undefined,
  switchGrowthspace: async () => {
    throw new Error("switchGrowthspace not implemented")
  },
  isGrowthSpaceUser: false,
  isNcte: false,
}

export const context = React.createContext<Context>(defaultContextValue)

export const useGlobalContext = () => useContext(context)
